import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, Calendar, Clock, MapPin, AlertTriangle } from 'lucide-react';
import { CiFilter } from "react-icons/ci";
import { useAuth } from "../context/AuthContext";
import { describeRecordLocation } from "../utils/geolocation";

const AttendanceHistory = ({ user, token }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [customDate, setCustomDate] = useState({ from: '', to: '' });
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const filterRef = useRef(null);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setFilterOpen(false);
        setShowCustomPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const convertToIST = (utcDate) => {
    if (!utcDate) return null;
    const date = new Date(utcDate);
    const formatted = date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
    // Ensure AM/PM is uppercase if en-IN formats it as am/pm
    return formatted.replace(/am/i, 'AM').replace(/pm/i, 'PM');
  };

  const fetchHistory = async () => {
    if (!token || !user?.email) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/attendances/history`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        cache: 'no-store'
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setHistory(Array.isArray(data) ? data : []);
    } catch {
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [user, token]);

  const formatDuration = (seconds) => {
    if (!seconds) return 'Active';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} hrs`;
  };

  // Filter logic
  const getFilteredHistory = () => {
    const now = new Date();

    const toISTMidnight = (date) => {
      // Convert a JS Date to IST midnight (start of day in IST)
      const ist = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
      ist.setHours(0, 0, 0, 0);
      return ist;
    };

    const recordDateIST = (utcDate) => {
      if (!utcDate) return null;
      return new Date(new Date(utcDate).toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    };

    switch (activeFilter) {
      case 'today': {
        const todayStart = toISTMidnight(now);
        return history.filter((r) => {
          const d = recordDateIST(r.check_in);
          return d && d >= todayStart;
        });
      }
      case 'yesterday': {
        const todayStart = toISTMidnight(now);
        const yestStart = new Date(todayStart);
        yestStart.setDate(yestStart.getDate() - 1);
        return history.filter((r) => {
          const d = recordDateIST(r.check_in);
          return d && d >= yestStart && d < todayStart;
        });
      }
      case 'this_week': {
        const todayIST = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
        const dayOfWeek = todayIST.getDay(); // 0=Sun
        const weekStart = new Date(todayIST);
        weekStart.setDate(todayIST.getDate() - dayOfWeek);
        weekStart.setHours(0, 0, 0, 0);
        return history.filter((r) => {
          const d = recordDateIST(r.check_in);
          return d && d >= weekStart;
        });
      }
      case 'this_month': {
        const todayIST = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
        const monthStart = new Date(todayIST.getFullYear(), todayIST.getMonth(), 1);
        return history.filter((r) => {
          const d = recordDateIST(r.check_in);
          return d && d >= monthStart;
        });
      }
      case 'custom': {
        if (!customDate.from && !customDate.to) return history;
        const from = customDate.from ? new Date(customDate.from + 'T00:00:00') : null;
        const to = customDate.to ? new Date(customDate.to + 'T23:59:59') : null;
        return history.filter((r) => {
          const d = recordDateIST(r.check_in);
          if (!d) return false;
          if (from && d < from) return false;
          if (to && d > to) return false;
          return true;
        });
      }
      default:
        return history;
    }
  };

  const filterOptions = [
    { key: 'all', label: 'All' },
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'this_week', label: 'This Week' },
    { key: 'this_month', label: 'This Month' },
    { key: 'custom', label: 'Custom Date' },
  ];

  const getFilterLabel = () => {
    const opt = filterOptions.find((f) => f.key === activeFilter);
    if (activeFilter === 'custom' && (customDate.from || customDate.to)) {
      return `${customDate.from || '...'} → ${customDate.to || '...'}`;
    }
    return opt?.label || 'All';
  };

  const handleFilterSelect = (key) => {
    setActiveFilter(key);
    if (key === 'custom') {
      setShowCustomPicker(true);
    } else {
      setFilterOpen(false);
      setShowCustomPicker(false);
    }
  };

  const handleCustomApply = () => {
    setFilterOpen(false);
    setShowCustomPicker(false);
  };

  const filteredHistory = getFilteredHistory();

  return (
    <div className="attendance-history">
      <div className="attendance-header">
        <div className="attendance-title">
          <Calendar size={20} />
          <h3>Attendance History</h3>
        </div>

        <div className="attendance-actions">
          {/* Refresh Button */}
          <button onClick={fetchHistory} disabled={loading} className="attendance-refresh" title="Refresh">
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
          </button>

          {/* Filter Dropdown */}
          <div className="filter-wrapper" ref={filterRef}>
            <button
              className={`filter-btn ${activeFilter !== 'all' ? 'filter-active' : ''}`}
              onClick={() => setFilterOpen((prev) => !prev)}
              title="Filter"
            >
              <CiFilter size={20} />
              {/* <span className="filter-label">{getFilterLabel()}</span> */}
            </button>

            {filterOpen && (
              <div className="filter-dropdown">
                {!showCustomPicker ? (
                  <ul>
                    {filterOptions.map((opt) => (
                      <li
                        key={opt.key}
                        className={activeFilter === opt.key ? 'active' : ''}
                        onClick={() => handleFilterSelect(opt.key)}
                      >
                        {opt.label}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="custom-picker">
                    <p className="custom-picker-title">Custom Range</p>
                    <label>
                      From
                      <input
                        type="date"
                        value={customDate.from}
                        max={customDate.to || undefined}
                        onChange={(e) => setCustomDate((prev) => ({ ...prev, from: e.target.value }))}
                      />
                    </label>
                    <label>
                      To
                      <input
                        type="date"
                        value={customDate.to}
                        min={customDate.from || undefined}
                        onChange={(e) => setCustomDate((prev) => ({ ...prev, to: e.target.value }))}
                      />
                    </label>
                    <div className="custom-picker-actions">
                      <button className="btn-back" onClick={() => setShowCustomPicker(false)}> Back</button>
                      <button className="btn-apply" onClick={handleCustomApply}>Apply</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="attendance-table-wrapper">
        <table className="attendance-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Status</th>
              <th>Location</th>
              <th>Check In</th>
              <th>Check Out</th>
              <th>Duration</th>
            </tr>
          </thead>
          <tbody>
            {filteredHistory.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-data">
                  {loading ? 'Loading...' : 'No attendance records found'}
                </td>
              </tr>
            ) : (
              filteredHistory.map((record) => {
                const checkInIST = convertToIST(record.check_in);
                const checkOutIST = convertToIST(record.check_out);
                const location = describeRecordLocation(record, 'check_in');
                return (
                  <tr key={record.id}>
                    <td>{checkInIST?.split(',')[0] || 'N/A'}</td>
                    <td>
                      <span className={`status-badge ${record.status ? record.status.toLowerCase() : 'present'}`}>
                        {record.status === 'APPROVED' ? 'Exception/WFH' : (record.status || 'Present')}
                      </span>
                    </td>
                    <td>
                      {location ? (
                        <div className="location-cell">
                          <span
                            className={`location-badge location-${location.tone}`}
                            title={location.title}
                          >
                            <MapPin size={12} />
                            {location.label}
                            {location.lowConfidence && (
                              <AlertTriangle size={11} className="location-warn-icon" />
                            )}
                          </span>
                          {location.detail && (
                            <span className="location-detail">
                              {location.detail}
                              {location.mapUrl && (
                                <>
                                  {' '}
                                  <a
                                    className="location-map-link"
                                    href={location.mapUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    map
                                  </a>
                                </>
                              )}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="location-badge location-unavailable">—</span>
                      )}
                    </td>
                    <td>{checkInIST || 'N/A'}</td>
                    <td>{checkOutIST || '-'}</td>
                    <td>
                      <Clock size={16} />
                      <span className="duration">{formatDuration(record.duration_seconds)}</span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <style>{`
        .attendance-actions {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        /* Spinning refresh */
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 0.8s linear infinite;
        }

        /* Filter button */
        .filter-btn {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 5px 10px;
          border: 1.5px solid #d1d5db;
          border-radius: 6px;
          background: #fff;
          cursor: pointer;
          font-size: 13px;
          color: #374151;
          transition: border-color 0.2s, background 0.2s;
          white-space: nowrap;
        }
        .filter-btn:hover {
          border-color: #6366f1;
          background: #f5f3ff;
        }
        .filter-btn.filter-active {
          border-color: #6366f1;
          background: #f5f3ff;
          color: #6366f1;
          font-weight: 600;
        }
        .filter-label {
          max-width: 140px;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* Dropdown */
        .filter-wrapper {
          position: relative;
        }
        .filter-dropdown {
          position: absolute;
          right: 0;
          top: calc(100% + 6px);
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.12);
          z-index: 100;
          min-width: 160px;
          overflow: hidden;
          animation: fadeDown 0.15s ease;
        }
        @keyframes fadeDown {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .filter-dropdown ul {
          list-style: none;
          margin: 0;
          padding: 6px 0;
        }
        .filter-dropdown ul li {
          padding: 9px 16px;
          font-size: 13px;
          cursor: pointer;
          color: #374151;
          transition: background 0.15s;
        }
        .filter-dropdown ul li:hover {
          background: #f5f3ff;
          color: #6366f1;
        }
        .filter-dropdown ul li.active {
          background: #ede9fe;
          color: #6366f1;
          font-weight: 600;
        }

        /* Custom date picker */
        .custom-picker {
          padding: 12px 16px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          min-width: 220px;
        }
        .custom-picker-title {
          font-size: 13px;
          font-weight: 600;
          color: #374151;
          margin: 0;
        }
        .custom-picker label {
          display: flex;
          flex-direction: column;
          gap: 3px;
          font-size: 12px;
          color: #6b7280;
          font-weight: 500;
        }
        .custom-picker input[type="date"] {
          padding: 6px 8px;
          border: 1px solid #d1d5db;
          border-radius: 5px;
          font-size: 12px;
          color: #374151;
          outline: none;
          cursor: pointer;
        }
        .custom-picker input[type="date"]:focus {
          border-color: #6366f1;
        }
        .custom-picker-actions {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
        }
        .btn-back {
          padding: 5px 10px;
          font-size: 12px;
          border: 1px solid #d1d5db;
          border-radius: 5px;
          background: #fff;
          cursor: pointer;
          color: #6b7280;
        }
        .btn-back:hover {
          background: #f9fafb;
        }
        .btn-apply {
          padding: 5px 12px;
          font-size: 12px;
          border: none;
          border-radius: 5px;
          background: #6366f1;
          color: #fff;
          cursor: pointer;
          font-weight: 600;
        }
        .btn-apply:hover {
          background: #4f46e5;
        }
        .status-badge {
          display: inline-block;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          text-transform: capitalize;
        }
        .status-badge.present {
          background-color: #d1fae5;
          color: #065f46;
        }
        .status-badge.approved, .status-badge.exception {
          background-color: #dbeafe;
          color: #1e40af;
        }
        .status-badge.absent {
          background-color: #fee2e2;
          color: #b91c1c;
        }

        /* Location-based check-in status */
        .location-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          white-space: nowrap;
        }
        .location-badge.location-in-office {
          background-color: #d1fae5;
          color: #065f46;
        }
        .location-badge.location-out-office {
          background-color: #fef3c7;
          color: #92400e;
        }
        .location-badge.location-unavailable {
          background-color: #e5e7eb;
          color: #6b7280;
        }
        .location-cell {
          display: flex;
          flex-direction: column;
          gap: 4px;
          align-items: flex-start;
        }
        .location-detail {
          font-size: 11px;
          color: #64748b;
          line-height: 1.4;
          max-width: 260px;
          word-break: break-word;
        }
        .location-map-link {
          color: #0ea5e9;
          text-decoration: none;
          white-space: nowrap;
        }
        .location-map-link:hover { text-decoration: underline; }
        .location-warn-icon { margin-left: 2px; }
      `}</style>
    </div>
  );
};

export default AttendanceHistory;